/**
 * External dependencies
 */
import cx from 'classnames';
import { Link, usePage } from '@inertiajs/react';

/**
 * Internal dependencies
 */
import Button from '@/common/components/button/button';
import Input from '@/common/components/input/input';
import PasswordInput from '@/common/components/password-input/password-input';
import ValidationErrors from '@/common/components/errors/validation-errors';
import LogoAlt from '@/assets/images/logo-alt.svg';
import FrontdeskLogoAlt from '@/assets/images/frontdesk-logo-header.svg';
import ButtonLoader from '@/hosts-marketplace/components/button-loader/button-loader';

const FormLogin = ({ data, submit, processing, isLoading, onHandleChange, status, canResetPassword, errors }) => {
    const { isShop, externalLinks } = usePage().props;

    const goToGoogleAuth = () => {
        window.location.href = route('google.auth', { platform: 'shop' });
    };

    if (errors.hasOwnProperty('email') || errors.hasOwnProperty('password')) {
        delete errors['googleAccountDeleted'];
    }

    return (
        <form onSubmit={submit} className="form-guest form-guest--login">
            <div className="form-guest__image fd-by-ih">
                <a href={externalLinks?.inhouse?.main}>
                    <img src={LogoAlt} alt="" />
                </a>
            </div>

            <div className="form-guest__head">
                <h1 className="h1 form-guest__title">Sign In</h1>
            </div>

            {status && <div className="mb-4 color-success">{status}</div>}

            <ValidationErrors errors={errors} />

            <div className="form-guest__body">
                <div>
                    <Input
                        type="email"
                        name="email"
                        className="field--floating"
                        label="Email"
                        value={data.email}
                        autoComplete="username"
                        isFocused={true}
                        errorText={errors.email}
                        handleChange={onHandleChange}
                    />
                </div>

                <div>
                    <PasswordInput
                        name="password"
                        className="field--floating"
                        label="Password"
                        value={data.password}
                        errorText={errors.password}
                        autoComplete="current-password"
                        handleChange={onHandleChange}
                    />
                </div>
            </div>

            <div className="form-guest__actions">
                <div>
                    <ButtonLoader
                        className="btn--theme btn--full-w"
                        type="submit"
                        loading={isLoading || processing}
                        allowShowCheckOnLoad={false}
                    >
                        Sign In
                    </ButtonLoader>
                </div>

                {!isShop && (
                    <div>
                        <Button className="btn--full-w form-guest__google__btn" onClick={goToGoogleAuth}>
                            <svg
                                width="17"
                                height="17"
                                viewBox="0 0 17 17"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M4.3611 7.13124C4.64767 6.26424 5.20072 5.50989 5.94136 4.9758C6.68201 4.44171 7.5724 4.15517 8.48553 4.15704C9.52061 4.15704 10.4565 4.52453 11.1915 5.12598L13.3302 2.98782C12.0269 1.85168 10.3566 1.15039 8.48553 1.15039C5.58851 1.15039 3.0945 2.80286 1.89526 5.22337L4.3611 7.13124Z"
                                    fill="#EA4335"
                                />
                                <path
                                    d="M10.9593 12.1824C10.2917 12.613 9.44406 12.8427 8.48492 12.8427C7.57544 12.8445 6.6884 12.5603 5.94941 12.0301C5.21041 11.5 4.6569 10.7508 4.36723 9.88867L1.89282 11.7671C2.49994 12.9959 3.43949 14.0299 4.60477 14.7515C5.77004 15.4731 7.11432 15.8534 8.48492 15.8493C10.2813 15.8493 11.9975 15.2105 13.2831 14.0119L10.9599 12.1824H10.9593Z"
                                    fill="#34A853"
                                />
                                <path
                                    d="M13.283 14.0128C14.6274 12.7584 15.5002 10.8916 15.5002 8.50049C15.5002 8.06563 15.4334 7.59831 15.3336 7.16406H8.48486V10.0041H12.4268C12.2326 10.959 11.7102 11.6982 10.9599 12.1833L13.283 14.0128Z"
                                    fill="#4A90E2"
                                />
                                <path
                                    d="M4.36786 9.89047C4.21802 9.44269 4.1419 8.97355 4.14247 8.50137C4.14247 8.02241 4.21903 7.56244 4.36112 7.13248L1.89529 5.22461C1.39084 6.24297 1.13079 7.36492 1.13581 8.50137C1.13581 9.67732 1.40837 10.7859 1.89345 11.7689L4.36786 9.89047Z"
                                    fill="#FBBC05"
                                />
                            </svg>
                            Continue with Google
                        </Button>
                    </div>
                )}
            </div>

            <div className="form-guest__bottom">
                <p className="color-dark-100">
                    No account?{' '}
                    {isShop ? (
                        <a href={externalLinks?.inhouse?.contacts} style={{ textDecoration: 'underline' }}>
                            Join the waitlist
                        </a>
                    ) : (
                        <a
                            href={isShop ? route('register') : route('register', { platform: 'frontdesk' })}
                            style={{ textDecoration: 'underline' }}
                        >
                            Create one
                        </a>
                    )}
                </p>
            </div>

            {canResetPassword && (
                <div className="form-guest__additional form-guest__forgot-password">
                    <Link href={route('shop.password.request')}>Forgot Password?</Link>
                </div>
            )}
        </form>
    );
};

export default FormLogin;
